import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cx } from './cx';

/**
 * Small status / category label. Replaces the per-page status pills (LotCard
 * "Live", role badges, "Sold", counts). Tinted (subtle) by default; `solid`
 * fills with the tone colour for the loudest cases (an accent "Live now").
 */
type BadgeTone = 'neutral' | 'ink' | 'accent' | 'danger' | 'success' | 'warning';

const SUBTLE: Record<BadgeTone, string> = {
  neutral: 'bg-bone text-text-muted',
  ink: 'bg-bone text-ink',
  accent: 'bg-accent/12 text-accent',
  danger: 'bg-danger-bg text-danger',
  success: 'bg-success-bg text-success',
  warning: 'bg-warning-bg text-warning',
};

const SOLID: Record<BadgeTone, string> = {
  neutral: 'bg-text-muted text-bg',
  ink: 'bg-ink text-bg',
  accent: 'bg-accent text-accent-ink',
  danger: 'bg-danger text-bg',
  success: 'bg-success text-bg',
  warning: 'bg-warning text-bg',
};

export function Badge({
  tone = 'neutral',
  solid = false,
  className,
  children,
  ...rest
}: {
  tone?: BadgeTone;
  solid?: boolean;
  className?: string;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<'span'>, 'className' | 'children'>) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5',
        'font-body text-[0.6875rem] font-semibold uppercase tracking-[0.08em] leading-none',
        solid ? SOLID[tone] : SUBTLE[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
